import { getHttpClient } from "@/legacy/features/authentication/auth-http-client.service";
import { DelegatedOnboardingService } from "../interfaces/delegated-onboarding-service";
import {
  DelegatedOnboardingRequest,
  DelegatedOnboardingResponse,
  DelegatedOnboardingResponseErrorCode,
  GetAcsidStatusRequest,
  GetEmailStatusRequest,
} from "../models/delegated-onboarding.models";
import { IdentityPortrayalType } from "@/legacy/models/shared";
import Vue from "vue";

interface ConfirmOnboardingRawRequestWithoutDocumentWithId {
  username: string;
  acs_id: string;
  acs_id_schema: string;
  email: string;
  portrayal_type: IdentityPortrayalType.selfie;
  selfie: string;
  deliver_by_email: boolean;
}

interface ConfirmSingleDynamicDocumentData {
  [key: string]: string | boolean | number;
}

type ConfirmOnboardingRawRequest = ConfirmOnboardingRawRequestWithoutDocumentWithId | ConfirmSingleDynamicDocumentData;

interface ConfirmDelegatedOnboardingRawResponse {
  onboarding_id: string;
  mode: "auto" | "assisted";
  status: "initialized" | "running" | "completed" | "cancelled" | "failed" | "finished";
  result?: {
    outcome: "accepted" | "rejected";
    reason:
      | "selfie_not_provided"
      | "embedding_generation_failed"
      | "ocr_incomplete"
      | "identity_verification_below_th"
      | "ocr_adaptations_not_enabled"
      | "validation_failed"
      | "acs_id_already_in_use"
      | "qr_generation_failed";
  };
}

export class HttpDelegatedOnboardingService extends Vue implements DelegatedOnboardingService {
  public async acsidExists(request: GetAcsidStatusRequest): Promise<boolean> {
    const httpClient = getHttpClient();
    const rawResponse = await httpClient.get(`/public/credentials?acs_id=` + request.acsid, {
      params: { silent: true },
      validateStatus: status => {
        return status === 200 || status === 404;
      },
    });
    if (rawResponse.status === 200) {
      return true;
    }
    return false;
  }

  public async emailExists(request: GetEmailStatusRequest): Promise<boolean> {
    const httpClient = getHttpClient();
    const rawResponse = await httpClient.get(`/internal/accounts?email=` + request.email, {
      params: { silent: true },
      validateStatus: status => {
        return status === 200 || status === 404;
      },
    });
    if (rawResponse.status === 200) {
      return true;
    }
    return false;
  }

  public async single(request: DelegatedOnboardingRequest): Promise<DelegatedOnboardingResponse> {
    const httpClient = getHttpClient();
    let rawRequest: ConfirmOnboardingRawRequest;

    if ("fields" in request.data) {
      rawRequest = request.data.fields as ConfirmSingleDynamicDocumentData;
      if (request.data.email !== "") {
        rawRequest.email = request.data.email;
      }
      rawRequest.acs_id_schema = request.acsIdSchema;
      rawRequest.portrayal_type = request.type;
      rawRequest.deliver_by_email = request.data.deliver_by_mail;
      rawRequest.selfie = request.data.selfie;
    } else {
      rawRequest = {
        username: request.data.username,
        acs_id: request.data.acs_id,
        acs_id_schema: request.acsIdSchema,
        email: request.data.email,
        selfie: request.data.image,
        portrayal_type: request.type,
        deliver_by_email: request.data.deliver_by_mail,
      } as ConfirmOnboardingRawRequest;
    }

    const rawResponse = await httpClient.post<ConfirmDelegatedOnboardingRawResponse>(`/public/singles`, rawRequest);

    if (rawResponse.data.result?.outcome === "accepted") {
      return {
        success: true,
      };
    }

    let errorCode = DelegatedOnboardingResponseErrorCode.Unknown;
    switch (rawResponse.data.result?.reason) {
      case "selfie_not_provided":
        errorCode = DelegatedOnboardingResponseErrorCode.DocumentExpired;
        break;
      case "embedding_generation_failed":
        errorCode = DelegatedOnboardingResponseErrorCode.EmbeddingFailed;
        break;
      case "acs_id_already_in_use":
        errorCode = DelegatedOnboardingResponseErrorCode.IdAlreadyUsed;
        break;
      case "qr_generation_failed":
        errorCode = DelegatedOnboardingResponseErrorCode.QrGenerationFailed;
        break;
    }

    return {
      success: false,
      code: errorCode,
    };
  }
}
